// Utility functions for working with the icon system

import { IconData, addIcon } from './IconRegistry';

/**
 * Converts an SVG string to IconData format
 * @param svgString - The raw SVG string
 * @returns IconData object or null if parsing fails
 */
export const svgStringToIconData = (svgString: string): IconData | null => {
  try {
    // Create a temporary DOM element to parse the SVG
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    
    if (!svg) {
      console.error('No SVG element found in the provided string');
      return null;
    }

    // Extract viewBox
    const viewBox = svg.getAttribute('viewBox') || '0 0 24 24';
    
    // Extract all path elements
    const pathElements = svg.querySelectorAll('path');
    const paths = Array.from(pathElements).map(path => path.getAttribute('d')).filter(Boolean) as string[];
    
    if (paths.length === 0) {
      console.error('No path elements found in the SVG');
      return null;
    }

    return {
      viewBox,
      paths
    };
  } catch (error) {
    console.error('Error parsing SVG string:', error);
    return null;
  }
};

/**
 * Adds an icon from an SVG string to the registry
 * @param name - The name for the icon
 * @param svgString - The raw SVG string
 * @returns boolean indicating success
 */
export const addIconFromSvg = (name: string, svgString: string): boolean => {
  const iconData = svgStringToIconData(svgString);
  if (iconData) {
    addIcon(name, iconData);
    return true;
  }
  return false;
};

/**
 * Generates TypeScript code for adding an icon to the registry
 * @param name - The name for the icon
 * @param svgString - The raw SVG string
 * @returns TypeScript code string or null if parsing fails
 */
export const generateIconCode = (name: string, svgString: string): string | null => {
  const iconData = svgStringToIconData(svgString);
  if (!iconData) return null;

  const pathsCode = iconData.paths.map(path => `      "${path}"`).join(',\n');
  
  return `  ${name}: {
    viewBox: "${iconData.viewBox}",
    paths: [
${pathsCode}
    ]
  },`;
};

/**
 * Batch process multiple SVG files
 * @param svgFiles - Array of objects with name and svgString
 * @returns Array of generated TypeScript code
 */
export const batchGenerateIconCode = (svgFiles: Array<{name: string, svgString: string}>): string[] => {
  return svgFiles.map(file => generateIconCode(file.name, file.svgString)).filter(Boolean) as string[];
};

/**
 * Common icon size presets
 */
export const IconSizes = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64
} as const;

export type IconSize = keyof typeof IconSizes;

/**
 * Common color presets (you can customize these based on our approved design system)
 */
export const IconColors = {
  primary: '#3B82F6',
  secondary: '#6B7280',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  muted: '#9CA3AF',
  white: '#FFFFFF',
  black: '#000000',
  orange: '#FE6434'
} as const;

export type IconColor = keyof typeof IconColors; 